import { useCallback, useState } from 'react'
import { Loader2, Send } from 'lucide-react'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { Drawer, DrawerField, DrawerSection } from '@/shared/ui/Drawer'
import { Modal } from '@/shared/ui/Modal'
import { EmptyState, ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { UnitMap } from '@/shared/map/UnitMap'
import { hasMapbox } from '@/shared/map/mapbox'
import {
  acknowledgeIncident, addComment, assignIncident, closeIncident, completeAction,
  createAction, escalateIncident, reopenIncident, resolveIncident, verifyIncident,
  type Incident,
} from './api'
import { useIncident, useVocabulary } from './useIncident'
import { EntityPicker, useEntityLabel } from '@/shared/pickers/EntityPicker'
import { EvidencePanel } from './EvidencePanel'
import { SeverityChip, StatusChip, VerificationChip, ageOf, label } from './vocab'
import { DatePicker } from '@/components/erp/date-picker'

/**
 * The detail drawer: summary, timeline, evidence, follow-up actions.
 *
 * ## Every action reloads the incident AND the timeline
 *
 * `useIncident` reloads all four collections together, because an action that visibly
 * changes nothing reads as a failure and gets clicked again. Escalating writes a timeline
 * row and changes the status; reloading one without the other leaves half the drawer
 * stale.
 *
 * ## Every action is offered
 *
 * Including to a caller who may not hold its authority. The server refuses with a sentence
 * naming the permission, and that sentence is what appears. Hiding the button instead
 * teaches nothing and makes a missing grant indistinguishable from a missing feature.
 */

/*
   Nine tabs, not one scrolling summary.

   The summary had six sections stacked in a 640px drawer, so answering "where did this
   happen" or "what does the court file say" meant scrolling past everything else. Each
   section is now addressable in one click. The bar scrolls horizontally rather than
   wrapping — a wrapped tab bar changes height as you move through it and pushes the
   content down under your cursor.
*/
type Tab = 'summary' | 'location' | 'impact' | 'attribution' | 'lifecycle' | 'legal'
  | 'timeline' | 'evidence' | 'actions'

const TABS: { key: Tab; label: string }[] = [
  { key: 'summary', label: 'Summary' },
  { key: 'location', label: 'Location' },
  { key: 'impact', label: 'Impact' },
  { key: 'attribution', label: 'Attribution' },
  { key: 'lifecycle', label: 'Lifecycle' },
  { key: 'legal', label: 'Legal' },
  { key: 'timeline', label: 'Timeline' },
  { key: 'evidence', label: 'Evidence' },
  { key: 'actions', label: 'Actions' },
]

export function IncidentDrawer({
  id, onClose, onChanged,
}: {
  id: string | null
  onClose: () => void
  onChanged?: () => void
}) {
  const { incident, timeline, evidence, actions, error, loading, reload } = useIncident(id)
  const [tab, setTab] = useState<Tab>('summary')
  const [action, setAction] = useState<ActionKind | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const after = useCallback(async () => {
    await reload()
    onChanged?.()
  }, [reload, onChanged])

  return (
    <Drawer
      open={id !== null}
      onClose={onClose}
      width="lg"
      title={incident ? incident.title : 'Incident'}
      subtitle={incident ? `${incident.ref} · reported ${ageOf(incident.reportedAt)} ago` : undefined}
    >
      {loading && !incident && <LoadingBlock label="Loading the incident…" />}
      <ErrorStrip message={error ?? actionError} />

      {incident && (
        <>
          <div className="px-4 py-3 flex flex-wrap items-center gap-2 border-b border-border">
            <StatusChip code={incident.statusCode} />
            <VerificationChip code={incident.verificationCode} />
            <SeverityChip code={incident.severityCode} />
            <span className="text-erp-xs text-muted-foreground">{label(incident.priorityCode)}</span>
          </div>

          {/* Status and verification never merge, and the drawer says why in one line so a
              reader meeting the desk for the first time is not left to infer it. */}
          <p className="px-4 py-2 text-erp-xs text-muted-foreground border-b border-border/60">
            <strong className="text-foreground">{label(incident.statusCode)}</strong> is where
            this is in the workflow.{' '}
            <strong className="text-foreground">{label(incident.verificationCode)}</strong> is
            how much the desk believes it. They move independently.
          </p>

          <ActionBar
            incident={incident}
            onPick={(kind) => { setActionError(null); setAction(kind) }}
          />

          <nav className="px-4 border-b border-border axis-table-scroll">
            <div className="flex gap-1 min-w-max">
              {TABS.map((t) => (
                <button key={t.key} type="button" onClick={() => setTab(t.key)}
                  className={cn(
                    'px-3 py-2 text-erp-xs font-medium border-b-2 -mb-px whitespace-nowrap',
                    tab === t.key
                      ? 'border-chocolate text-chocolate'
                      : 'border-transparent text-muted-foreground hover:text-foreground',
                  )}>
                  {t.label}
                  {t.key === 'evidence' && incident.evidenceCount > 0 && ` (${incident.evidenceCount})`}
                  {t.key === 'actions' && incident.openActionCount > 0 && ` (${incident.openActionCount})`}
                </button>
              ))}
            </div>
          </nav>

          {tab === 'summary' && <SummaryTab incident={incident} />}
          {tab === 'location' && <LocationTab incident={incident} />}
          {tab === 'impact' && <ImpactTab incident={incident} />}
          {tab === 'attribution' && <AttributionTab incident={incident} />}
          {tab === 'lifecycle' && <LifecycleTab incident={incident} />}
          {tab === 'legal' && <LegalTab incident={incident} />}
          {tab === 'timeline' && (
            <TimelineTab incidentId={incident.id} rows={timeline} onCommented={after} />
          )}
          {tab === 'evidence' && (
            <div className="p-4">
              <EvidencePanel
                incidentId={incident.id}
                canAttach={incident.statusCode !== 'CLOSED'}
                onChanged={after}
              />
            </div>
          )}
          {tab === 'actions' && (
            <ActionsTab incidentId={incident.id} rows={actions} onChanged={after} />
          )}

          <ActionDialog
            kind={action}
            incident={incident}
            onClose={() => setAction(null)}
            onDone={async () => { setAction(null); await after() }}
            onError={setActionError}
          />

          {/* Kept mounted so the evidence count in the tab bar stays honest even while
              another tab is showing. Reading `evidence` here is what makes that true. */}
          <span className="sr-only">{evidence.length} evidence items</span>
        </>
      )}
    </Drawer>
  )
}

// ── the six detail tabs ─────────────────────────────────────────────────────

/*
   Every value in these panels is rendered through `label()` or a resolved name — never a
   raw code and never a raw uuid. A record whose purpose is to be read back months later
   by somebody who was not there cannot say "UNIT_CENTROID" and "01a0336c-83fb-…" and
   expect to be understood.
*/

function SummaryTab({ incident }: { incident: Incident }) {
  return (
    <div>
      {incident.description && (
        <p className="px-4 py-3 text-erp-sm whitespace-pre-wrap border-b border-border/60">
          {incident.description}
        </p>
      )}
      <DrawerSection title="Classification">
        <DrawerField label="Category" value={label(incident.categoryCode)} />
        <DrawerField label="Subcategory" value={incident.subcategory} />
        <DrawerField label="Severity" value={label(incident.severityCode)} />
        <DrawerField label="Priority" value={label(incident.priorityCode)} />
        <DrawerField label="Occurred"
          value={incident.occurredAt
            ? `${new Date(incident.occurredAt).toLocaleString()} · known ${label(incident.occurredAtPrecisionCode).toLowerCase()}`
            : 'not stated'} />
        <DrawerField label="Reported" value={new Date(incident.reportedAt).toLocaleString()} />
        <DrawerField label="Reported by" value={incident.reportedByName} />
        <DrawerField label="Assigned to" value={incident.assignedToName ?? 'nobody yet'} />
      </DrawerSection>

      {incident.tags.length > 0 && (
        <div className="px-4 py-3 flex flex-wrap gap-1.5 border-t border-border/60">
          {incident.tags.map((t) => (
            <span key={t} className="rounded-full border border-border bg-muted/40 px-2 py-0.5 text-erp-xs">
              {t}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

function LocationTab({ incident }: { incident: Incident }) {
  const placed = incident.latitude != null && incident.longitude != null
  return (
    <div>
      {placed && hasMapbox() && (
        <div className="px-4 pt-4">
          <UnitMap mode="point" label={incident.title}
            lat={incident.latitude} lon={incident.longitude}
            className="h-56 rounded-md overflow-hidden border border-border" />
        </div>
      )}
      <DrawerSection title="Where it happened">
        <DrawerField label="Unit" value={incident.unitName} />
        <DrawerField label="Place" value={incident.locationLabel} />
        <DrawerField label="Coordinate"
          value={placed ? `${incident.latitude}, ${incident.longitude}` : null} mono />
        {/* HOW the coordinate was obtained is evidence in itself: a 2 km wifi estimate and
            a pin dropped on a map are not the same fact. */}
        <DrawerField label="How it was obtained"
          value={incident.locationSourceCode ? label(incident.locationSourceCode) : null} />
        <DrawerField label="Accuracy"
          value={incident.locationAccuracyM != null
            ? `±${incident.locationAccuracyM} m${incident.locationAccuracyM > 300 ? ' — a network estimate, not a device fix' : ''}`
            : null} />
        <DrawerField label="Not known because" value={incident.locationAbsentReason} />
      </DrawerSection>
      {!placed && !incident.unitId && !incident.locationAbsentReason && (
        <EmptyState title="No location on this report"
          hint="Neither a unit, a coordinate, nor a stated reason there is none." />
      )}
    </div>
  )
}

function ImpactTab({ incident }: { incident: Incident }) {
  const counted = [incident.victimCount, incident.injuryCount, incident.deathCount,
    incident.arrestsMade, incident.estimatedVoterImpact].some((v) => v != null)
  if (!counted) {
    return (
      <EmptyState title="Nothing counted yet"
        hint="Every impact figure is optional, and empty means “not known” — which is a different fact from zero." />
    )
  }
  return (
    <DrawerSection title="Impact">
      <DrawerField label="People affected" value={incident.victimCount} />
      <DrawerField label="Injuries" value={incident.injuryCount} />
      <DrawerField label="Deaths" value={incident.deathCount} />
      <DrawerField label="Arrests" value={incident.arrestsMade} />
      <DrawerField label="Voters affected" value={incident.estimatedVoterImpact} />
    </DrawerSection>
  )
}

function AttributionTab({ incident }: { incident: Incident }) {
  // Resolved to NAMES. A uuid in a record about who is alleged to have done something is
  // not a record anybody can read.
  const perpetratorParty = useEntityLabel('party', incident.perpetratorPartyId)
  const relatedParty = useEntityLabel('party', incident.relatedPartyId)
  const relatedCandidate = useEntityLabel('candidate', incident.relatedCandidateId)

  return (
    <div>
      <p className="px-4 py-2.5 text-erp-xs text-muted-foreground border-b border-border/60">
        Recorded as <strong className="text-foreground">alleged</strong>, not as a finding.
        Whether the desk believes it is the verification record on the Lifecycle tab.
      </p>
      <DrawerSection title="Who">
        <DrawerField label="Alleged perpetrator" value={incident.perpetrator} />
        <DrawerField label="Their party" value={perpetratorParty} />
        <DrawerField label="Related candidate" value={relatedCandidate} />
        <DrawerField label="Related party" value={relatedParty} />
      </DrawerSection>
      <DrawerSection title="Where the claim came from">
        <DrawerField label="Source outlet" value={incident.sourceOutlet} />
        <DrawerField label="Source link" value={incident.sourceUrl
          ? <a className="text-chocolate underline break-all" href={incident.sourceUrl}
            target="_blank" rel="noreferrer noopener">{incident.sourceUrl}</a>
          : null} />
        <DrawerField label="Published at"
          value={incident.sourcePublishedAt
            ? new Date(incident.sourcePublishedAt).toLocaleString() : null} />
        <DrawerField label="Media coverage" value={label(incident.mediaCoverageCode)} />
      </DrawerSection>
    </div>
  )
}

function LifecycleTab({ incident }: { incident: Incident }) {
  const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : null)
  return (
    <div>
      <p className="px-4 py-2.5 text-erp-xs text-muted-foreground border-b border-border/60">
        <strong className="text-foreground">{label(incident.statusCode)}</strong> is where
        this is in the workflow.{' '}
        <strong className="text-foreground">{label(incident.verificationCode)}</strong> is
        how much the desk believes it. They move independently — an incident can be
        resolved and still unverified.
      </p>
      <DrawerSection title="Workflow">
        <DrawerField label="Status" value={label(incident.statusCode)} />
        <DrawerField label="Acknowledged" value={when(incident.acknowledgedAt)} />
        <DrawerField label="Escalated to" value={incident.escalatedTo} />
        <DrawerField label="Escalated" value={when(incident.escalatedAt)} />
        <DrawerField label="Resolved" value={when(incident.resolvedAt)} />
        <DrawerField label="Resolution"
          value={incident.resolutionCode ? label(incident.resolutionCode) : null} />
        <DrawerField label="Resolution note" value={incident.resolutionNote} />
        <DrawerField label="Closed" value={when(incident.closedAt)} />
      </DrawerSection>
      <DrawerSection title="Belief">
        <DrawerField label="Verification" value={label(incident.verificationCode)} />
        <DrawerField label="Note" value={incident.verificationNote} />
      </DrawerSection>
    </div>
  )
}

function LegalTab({ incident }: { incident: Incident }) {
  const any = [incident.iebcComplaintRef, incident.dciCaseRef, incident.courtCaseRef,
    incident.ncicRef].some(Boolean)
  if (!any) {
    return (
      <EmptyState title="No legal reference recorded"
        hint="A complaint or case number is added once one exists — most do not at the moment of reporting." />
    )
  }
  return (
    <DrawerSection title="Legal references">
      <DrawerField label="IEBC complaint" value={incident.iebcComplaintRef} mono />
      <DrawerField label="DCI case" value={incident.dciCaseRef} mono />
      <DrawerField label="Court case" value={incident.courtCaseRef} mono />
      <DrawerField label="NCIC" value={incident.ncicRef} mono />
    </DrawerSection>
  )
}

// ── timeline ────────────────────────────────────────────────────────────────

function TimelineTab({
  incidentId, rows, onCommented,
}: {
  incidentId: string
  rows: { id: string; actionCode: string; fromValue: string | null; toValue: string | null
    note: string | null; actorName: string | null; createdAt: string }[]
  onCommented: () => void | Promise<void>
}) {
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  return (
    <div>
      <div className="p-4 border-b border-border">
        <ErrorStrip message={error} className="rounded-md border mb-2" />
        <div className="flex items-start gap-2">
          <textarea rows={2} value={note} placeholder="Add a comment to the record…"
            onChange={(e) => setNote(e.target.value)}
            className="erp-input flex-1 py-2 min-w-0" />
          <button type="button" disabled={busy || !note.trim()}
            onClick={async () => {
              setBusy(true); setError(null)
              try { await addComment(incidentId, note.trim()); setNote(''); await onCommented() }
              catch (err) { setError(explain(err)) } finally { setBusy(false) }
            }}
            className="erp-btn-primary h-9 px-3 shrink-0 inline-flex items-center gap-1.5 text-erp-sm">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            Post
          </button>
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyState title="Nothing on the timeline yet"
          hint="Every transition writes a row here, and nothing is ever removed — a reversal is a new row." />
      ) : (
        <ol className="divide-y divide-border/60">
          {rows.map((r) => (
            <li key={r.id} className="px-4 py-2.5">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-erp-sm font-medium">{label(r.actionCode)}</span>
                <span className="text-erp-xs text-muted-foreground shrink-0">
                  {new Date(r.createdAt).toLocaleString()}
                </span>
              </div>
              {(r.fromValue || r.toValue) && (
                <div className="text-erp-xs text-muted-foreground">
                  {r.fromValue ? label(r.fromValue) : '—'} → {r.toValue ? label(r.toValue) : '—'}
                </div>
              )}
              {r.note && <p className="text-erp-sm mt-0.5 whitespace-pre-wrap">{r.note}</p>}
              <div className="text-erp-xs text-muted-foreground mt-0.5">
                {r.actorName ?? 'the system'}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}

// ── follow-up actions ───────────────────────────────────────────────────────

function ActionsTab({
  incidentId, rows, onChanged,
}: {
  incidentId: string
  rows: { id: string; description: string; dueOn: string | null; assignedToName: string | null
    completedAt: string | null; open: boolean }[]
  onChanged: () => void | Promise<void>
}) {
  const [description, setDescription] = useState('')
  const [dueOn, setDueOn] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  return (
    <div>
      <div className="p-4 border-b border-border space-y-2">
        <ErrorStrip message={error} className="rounded-md border" />
        <input className="erp-input h-9 w-full" value={description}
          placeholder="What still needs doing?"
          onChange={(e) => setDescription(e.target.value)} />
        <div className="flex gap-2">
          <DatePicker value={dueOn} onChange={setDueOn} className="h-9 flex-1" />
          <button type="button" disabled={busy || !description.trim()}
            onClick={async () => {
              setBusy(true); setError(null)
              try {
                await createAction(incidentId, {
                  description: description.trim(), dueOn: dueOn || null,
                })
                setDescription(''); setDueOn(''); await onChanged()
              } catch (err) { setError(explain(err)) } finally { setBusy(false) }
            }}
            className="erp-btn-primary h-9 px-3 text-erp-sm shrink-0">
            Add
          </button>
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyState title="No follow-up actions"
          hint="A checklist of what the desk still owes this incident — referrals to make, people to call back." />
      ) : (
        <ul className="divide-y divide-border/60">
          {rows.map((a) => (
            <li key={a.id} className="px-4 py-2.5 flex items-start gap-3">
              <input type="checkbox" checked={!a.open} disabled={!a.open} className="mt-1"
                onChange={async () => {
                  setError(null)
                  try { await completeAction(incidentId, a.id); await onChanged() }
                  catch (err) { setError(explain(err)) }
                }} />
              <div className="min-w-0 flex-1">
                <div className={cn('text-erp-sm', !a.open && 'line-through text-muted-foreground')}>
                  {a.description}
                </div>
                <div className="text-erp-xs text-muted-foreground">
                  {a.dueOn ? `Due ${new Date(a.dueOn).toLocaleDateString()}` : 'No due date'}
                  {a.assignedToName && ` · ${a.assignedToName}`}
                  {a.completedAt && ` · done ${new Date(a.completedAt).toLocaleDateString()}`}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ── the action bar ──────────────────────────────────────────────────────────

type ActionKind = 'assign' | 'acknowledge' | 'escalate' | 'verify' | 'resolve' | 'close' | 'reopen'

const ACTION_LABELS: Record<ActionKind, string> = {
  assign: 'Assign', acknowledge: 'Acknowledge', escalate: 'Escalate',
  verify: 'Verify', resolve: 'Resolve', close: 'Close', reopen: 'Reopen',
}

function ActionBar({
  incident, onPick,
}: {
  incident: Incident
  onPick: (kind: ActionKind) => void
}) {
  const kinds: ActionKind[] = incident.statusCode === 'CLOSED'
    ? ['reopen']
    : ['assign', 'acknowledge', 'escalate', 'verify', 'resolve', 'close']

  return (
    <div className="px-4 py-2 flex flex-wrap gap-1.5 border-b border-border">
      {kinds.map((k) => (
        <button key={k} type="button" onClick={() => onPick(k)}
          className="erp-btn h-8 px-2.5 text-erp-xs">
          {ACTION_LABELS[k]}
        </button>
      ))}
    </div>
  )
}

/**
 * One dialog for every action.
 *
 * A note is REQUIRED wherever the backend requires one — escalate needs a destination,
 * resolve needs a resolution code. Where the backend does not require a note the field is
 * still offered, because "why" is the thing a record is read for six months later.
 */
function ActionDialog({
  kind, incident, onClose, onDone, onError,
}: {
  kind: ActionKind | null
  incident: Incident
  onClose: () => void
  onDone: () => void | Promise<void>
  onError: (message: string | null) => void
}) {
  const { vocab } = useVocabulary()
  const [note, setNote] = useState('')
  const [primary, setPrimary] = useState('')
  const [busy, setBusy] = useState(false)

  const needsPrimary = kind === 'assign' || kind === 'escalate' || kind === 'verify' || kind === 'resolve'

  const run = async () => {
    if (!kind) return
    setBusy(true)
    onError(null)
    try {
      switch (kind) {
        case 'assign': await assignIncident(incident.id, primary, note || null); break
        case 'acknowledge': await acknowledgeIncident(incident.id); break
        case 'escalate': await escalateIncident(incident.id, primary, note || null); break
        case 'verify': await verifyIncident(incident.id, primary, note || null); break
        case 'resolve': await resolveIncident(incident.id, primary, note || null); break
        case 'close': await closeIncident(incident.id, note || null); break
        case 'reopen': await reopenIncident(incident.id, note || null); break
      }
      setNote(''); setPrimary('')
      await onDone()
    } catch (err) {
      // The server's sentence, verbatim. It names the authority on a 403 and the illegal
      // transition on a 409 — both of which teach more than anything invented here.
      onError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open={kind !== null} onClose={onClose}
      title={kind ? ACTION_LABELS[kind] : ''}
      description={kind ? ACTION_DESCRIPTIONS[kind] : undefined}
      footer={(
        <>
          <button type="button" className="erp-btn h-9 px-3 text-erp-sm" onClick={onClose}>Cancel</button>
          <button type="button" disabled={busy || (needsPrimary && !primary.trim())}
            onClick={() => void run()}
            className="erp-btn-primary h-9 px-3 text-erp-sm inline-flex items-center gap-2">
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {kind ? ACTION_LABELS[kind] : ''}
          </button>
        </>
      )}>
      <div className="space-y-3">
        {kind === 'assign' && (
          <div>
            <span className="block text-erp-xs text-muted-foreground mb-1">
              Assign to — search by name or email. Active accounts only.
            </span>
            {/* A searchable picker, not a uuid box. Assigning an incident to a mistyped id
                silently hands it to nobody, and the desk finds out when it ages out. */}
            <EntityPicker source="user" placeholder="Search people…"
              value={primary || null} onChange={(v) => setPrimary(v ?? '')} />
            <p className="mt-1 text-erp-xs text-muted-foreground/80">
              Whether they may act on it is the server's call — it refuses with a sentence
              naming what they are missing.
            </p>
          </div>
        )}
        {kind === 'escalate' && (
          <label className="block">
            <span className="block text-erp-xs text-muted-foreground mb-1">
              Referred to — required. The body that can act on it.
            </span>
            <input className="erp-input h-9 w-full" value={primary}
              placeholder="DCI Nairobi Regional HQ"
              onChange={(e) => setPrimary(e.target.value)} />
          </label>
        )}
        {kind === 'verify' && (
          <label className="block">
            <span className="block text-erp-xs text-muted-foreground mb-1">
              Verification — how much the desk believes it, against evidence.
            </span>
            <select className="erp-input h-9 w-full" value={primary}
              onChange={(e) => setPrimary(e.target.value)}>
              <option value="">Choose…</option>
              {(vocab?.verifications ?? []).map((c) => (
                <option key={c} value={c}>{label(c)}</option>
              ))}
            </select>
          </label>
        )}
        {kind === 'resolve' && (
          <label className="block">
            <span className="block text-erp-xs text-muted-foreground mb-1">
              Resolution — required. The finding somebody can report on.
            </span>
            <select className="erp-input h-9 w-full" value={primary}
              onChange={(e) => setPrimary(e.target.value)}>
              <option value="">Choose…</option>
              {(vocab?.resolutions ?? []).map((c) => (
                <option key={c} value={c}>{label(c)}</option>
              ))}
            </select>
          </label>
        )}
        <label className="block">
          <span className="block text-erp-xs text-muted-foreground mb-1">Note</span>
          <textarea rows={3} className="erp-input w-full py-2" value={note}
            onChange={(e) => setNote(e.target.value)} />
        </label>
      </div>
    </Modal>
  )
}

const ACTION_DESCRIPTIONS: Record<ActionKind, string> = {
  assign: 'Hands the incident to somebody. They are notified.',
  acknowledge: 'Records that the desk has seen it and stops the ageing clock.',
  escalate: 'Refers it to IEBC, DCI, NCIC or a court. The referral is notified.',
  verify: 'Sets how much the desk believes the report. Separate from where it is in the workflow.',
  resolve: 'Records a finding. The incident can still be unverified — those are different questions.',
  close: 'Closes it. Requires a resolution already recorded.',
  reopen: 'Returns it to OPEN and clears the resolved and closed timestamps. The timeline says so.',
}
